import { useSelectedAcademicYear } from '@/hooks/useSelectedAcademicYear';
import type { AcademicYear, Term } from '@/types/api';

export interface TermsInSelectedYear {
  /** Only the selected year's terms, in teaching order. */
  terms: Term[];
  /** The year those terms belong to. */
  selectedYear: AcademicYear | null;
  isLoading: boolean;
}

/**
 * The terms a page may work with: those of the globally selected academic year.
 *
 * The year lives on the shell's topbar and the term stays page-local — so a
 * page never asks "which year", only "which term", and the answer is always
 * drawn from the year in scope.
 *
 * This shares `useSelectedAcademicYear`'s single unfiltered `/api/terms` query
 * rather than refetching per year: the shell already loads it for the year
 * popover, so a page mounting this makes no extra request, and the terms a page
 * offers can never disagree with the term counts the popover shows.
 *
 * Both underlying endpoints are teacher-or-admin only, so this belongs on staff
 * pages. A student's or parent's page must not call it.
 */
export function useTermsInSelectedYear(): TermsInSelectedYear {
  const { selectedTerms, allTerms, selectedYear, isLoading } = useSelectedAcademicYear();

  // Terms predate academic years as rows, so a school can have terms and no
  // year to scope them by. The topbar pill hides itself in that case and this
  // agrees with it: with no year in scope there is nothing to filter on, and
  // filtering anyway would take the gradebook away from a school whose data is
  // merely old.
  return { terms: selectedYear ? selectedTerms : allTerms, selectedYear, isLoading };
}

/**
 * Keeps a term choice honest as the year changes.
 *
 * Switching year strands whatever term a page had selected — it belongs to the
 * year just left, and left as-is the page would keep showing that year's work
 * under the new year's name. Every term picker needs the same answer, so it
 * lives here rather than four times over.
 *
 * Returns the candidate when it is still one of `terms`, otherwise the first
 * term of the year, otherwise null for a year with no terms yet.
 */
export function resolveTermId(terms: Term[], candidate: string | null): string | null {
  if (candidate && terms.some((term) => term.id === candidate)) return candidate;
  return terms[0]?.id ?? null;
}
